import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

/**
 * The four lists the rest of the platform reads as though they were constants.
 *
 * They are not constants, and this is where somebody curates them. The screen
 * is built around two facts that the API enforces and the UI has to make
 * legible:
 *
 * - **Nothing is deleted.** Every row is referenced by a profile, a company or
 *   a listing, so the destructive-looking button says "Retire" and the count
 *   beside it says how much is pointing at the row.
 * - **A retired row is still shown.** This is the one list in the product that
 *   defaults to showing what everything else hides, because "why is this
 *   missing from the dropdown" is the question that brings people here.
 */
const TABS = [
  {
    kind: 'universities',
    label: 'Universities',
    usageLabel: 'students',
    icon: 'university',
    blurb: 'A student proves they are a student with an address on the email domain.',
  },
  {
    kind: 'industries',
    label: 'Industries',
    usageLabel: 'companies',
    icon: 'industry',
    blurb: 'What a company files itself under.',
  },
  {
    kind: 'job-categories',
    label: 'Job categories',
    usageLabel: 'listings',
    icon: 'category',
    blurb: 'Drives discovery filters. Categories may nest one level.',
  },
  {
    kind: 'skills',
    label: 'Skills',
    usageLabel: 'profiles and listings',
    icon: 'skill',
    blurb: 'Students add these as they type them, so the long tail is real.',
  },
];

const PAGE_SIZE = 25;

/**
 * One dropdown, four orders. Each maps to the API's `sort` and `order`; the
 * first is the default, so the entry just created is at the top.
 */
const SORTS = [
  { key: 'newest', label: 'Newest first', sort: 'created_at', order: 'desc' },
  { key: 'oldest', label: 'Oldest first', sort: 'created_at', order: 'asc' },
  { key: 'name-asc', label: 'Name A→Z', sort: 'name', order: 'asc' },
  { key: 'name-desc', label: 'Name Z→A', sort: 'name', order: 'desc' },
];

/** What a dropdown filled from one of these lists asks for. */
const ALPHABETICAL = { sort: 'name', order: 'asc' };

/** Lists whose Retire button is switched off for now. Remove a kind to bring it back. */
const RETIRE_SWITCHED_OFF = new Set(['universities', 'industries', 'job-categories', 'skills']);

/**
 * Which list, from the address.
 *
 * The four are separate entries in the System administration menu, so each one
 * is a real destination: linkable, bookmarkable, and the same screen after a
 * reload. Keying the list on `kind` remounts it when the reader moves between
 * them, which clears the page number and the search box without anything
 * having to remember to.
 */
export function AdminReferenceDataPage() {
  const { kind } = useParams();

  if (!TABS.some((entry) => entry.kind === kind)) {
    return <Navigate to={paths.admin.universities} replace />;
  }

  return <ReferenceList key={kind} kind={kind} />;
}

function ReferenceList({ kind }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [sortKey, setSortKey] = useState(SORTS[0].key);
  const [editing, setEditing] = useState(null);
  const [retiring, setRetiring] = useState(null);
  const [merging, setMerging] = useState(null);

  const queryClient = useQueryClient();
  const toast = useToast();
  const tab = TABS.find((entry) => entry.kind === kind);

  const params = {
    page,
    page_size: PAGE_SIZE,
    include_inactive: includeInactive,
    ...sortParams(sortKey),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['reference', kind, params],
    queryFn: () => adminApi.listReference(kind, params),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['reference'] });

  const save = useMutation({
    mutationFn: ({ id, body }) =>
      id ? adminApi.updateReference(kind, id, body) : adminApi.createReference(kind, body),
    onSuccess: () => {
      toast.success('Saved');
      setEditing(null);
      void refresh();
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save that')),
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }) => adminApi.setReferenceActive(kind, id, isActive),
    onSuccess: (_result, variables) => {
      toast.success(variables.isActive ? 'Restored' : 'Retired');
      setRetiring(null);
      void refresh();
    },
    onError: (stateError) => toast.error(errorMessage(stateError)),
  });

  const merge = useMutation({
    mutationFn: ({ survivorId, duplicateId }) => adminApi.mergeSkills(survivorId, duplicateId),
    onSuccess: (result) => {
      toast.success(
        `"${result.merged_name}" combined into "${merging?.name}". ` +
          `${result.students_moved} profiles and ${result.internships_moved} listings moved.`,
      );
      setMerging(null);
      void refresh();
    },
    onError: (mergeError) => toast.error(errorMessage(mergeError, 'Could not merge those')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  // The sort order is not a filter: it never empties the list, so it does not
  // count towards "Nothing matches these filters".
  const activeCount = [search !== '', includeInactive !== true].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setIncludeInactive(true);
    setSortKey(SORTS[0].key);
    setPage(1);
  }

  return (
    <div>
      <PageHeader
        title={tab.label}
        description={tab.blurb}
        action={<Button onClick={() => setEditing({})}>Add {singular(tab.label)}</Button>}
      />

      {/* The menu is the way in and these are the way across: comparing two of
          these lists is common enough that it should not be a trip back to the
          sidebar. Same four addresses either way. */}
      <SegmentedTabs
        tabs={TABS.map((entry) => ({ key: entry.kind, label: entry.label }))}
        value={kind}
        onChange={(key) => navigate(paths.admin.referenceData(key))}
        label="Reference list"
        className="mb-6"
      />

      <ListCard
        toolbarEnd={
          <>
            <Checkbox
              label="Show retired"
              checked={includeInactive}
              onChange={(event) => {
                setIncludeInactive(event.target.checked);
                setPage(1);
              }}
            />
            <Select
              label={`Sort ${tab.label.toLowerCase()}`}
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-40"
              value={sortKey}
              onChange={(event) => {
                setSortKey(event.target.value);
                setPage(1);
              }}
            >
              {SORTS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </Select>
            <SearchInput
              label={`Search ${tab.label.toLowerCase()}`}
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
              placeholder={`Find a ${singular(tab.label).toLowerCase()}`}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Reading the catalogue…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing matches these filters."
                description="No entry matches that search."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState title="Nothing here" description="Add the first one." />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList className="space-y-2">
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              compact
              accent={row.is_active ? 'success' : 'neutral'}
              leading={
                <BoxAvatar size="sm" icon={tab.icon} tone={row.is_active ? 'brand' : 'neutral'} />
              }
              title={row.name}
              badges={!row.is_active && <Badge tone="neutral">Retired</Badge>}
              subtitle={<span className="font-mono text-xs">{row.slug}</span>}
              facts={[
                { icon: 'users', tone: 'brand', text: `${row.usage} ${tab.usageLabel}` },
                ...detailFacts(kind, row),
              ]}
              // A compact row: extra-small labelled buttons keep it slim.
              actions={
                <CardActions>
                  {/* Merging skills is switched off for now. Restore the block
                      below to bring the "Merge" button (and its dialog) back.
                  {kind === 'skills' && row.is_active && (
                    <Button
                      variant="secondary"
                      size="xs"
                      title={`Merge ${row.name} into another skill`}
                      onClick={() => setMerging(row)}
                    >
                      <Icon name="merge" size={14} />
                      Merge
                    </Button>
                  )} */}
                  <Button
                    variant="tintBrand"
                    size="xs"
                    title={`Edit ${row.name}`}
                    onClick={() => setEditing(row)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                </CardActions>
              }
              // Retire is switched off on all four lists for now (see
              // RETIRE_SWITCHED_OFF); Restore stays, so
              // anything retired before can still come back.
              corner={
                row.is_active ? (
                  RETIRE_SWITCHED_OFF.has(kind) ? null : (
                    <Button
                      variant="ghostDanger"
                      size="xs"
                      title={`Retire ${row.name}`}
                      onClick={() => setRetiring(row)}
                    >
                      <Icon name="archive" size={14} />
                      Retire
                    </Button>
                  )
                ) : (
                  <Button
                    variant="ghostPositive"
                    size="xs"
                    title={`Restore ${row.name}`}
                    onClick={() => setActive.mutate({ id: row.id, isActive: true })}
                  >
                    <Icon name="restore" size={14} />
                    Restore
                  </Button>
                )
              }
            />
          ))}
        </BoxList>
      </ListCard>

      <EditDialog
        kind={kind}
        tab={tab}
        entry={editing}
        isSaving={save.isPending}
        onCancel={() => setEditing(null)}
        onSave={(body) => save.mutate({ id: editing?.id, body })}
      />

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire ${retiring?.name ?? ''}?`}
        // The whole point of this dialog: it is not a delete, and the number is
        // the thing that decides whether it is safe.
        message={
          retiring?.usage
            ? `${retiring.usage} ${tab.usageLabel} still point at this. Retiring keeps every one of them and only takes the entry out of the lists people pick from — you can restore it at any time.`
            : 'Nothing points at this yet. Retiring takes it out of the lists people pick from; you can restore it at any time.'
        }
        confirmLabel="Retire it"
        isLoading={setActive.isPending}
        onConfirm={() => setActive.mutate({ id: retiring.id, isActive: false })}
        onCancel={() => setRetiring(null)}
      />

      <MergeDialog
        skill={merging}
        isMerging={merge.isPending}
        onCancel={() => setMerging(null)}
        onConfirm={(duplicateId) => merge.mutate({ survivorId: merging.id, duplicateId })}
      />
    </div>
  );
}

function singular(label) {
  return { Universities: 'university', Industries: 'industry' }[label] ?? label.replace(/s$/, '');
}

/** What each list knows beyond a name, as the chips under it. */
function detailFacts(kind, row) {
  if (kind === 'universities') {
    const place = [row.city, row.country].filter(Boolean).join(', ');
    return [
      row.email_domain
        ? { icon: 'shieldCheck', text: <span className="font-mono">{row.email_domain}</span> }
        : // Worth calling out rather than leaving blank: without a domain the
          // row cannot verify anybody, which is most of its purpose.
          { icon: 'alert', tone: 'warning', text: 'No email domain — cannot verify students' },
      place && { icon: 'home', text: place },
    ];
  }
  if (kind === 'job-categories') {
    return [{ icon: 'category', text: row.parent_name ? `Under ${row.parent_name}` : 'Top level' }];
  }
  if (kind === 'skills') {
    return [
      row.category && { icon: 'tag', text: row.category },
      row.aliases?.length > 0 && {
        icon: 'merge',
        text: `also known as ${row.aliases.map((a) => a.alias).join(', ')}`,
      },
      { icon: 'student', text: `${row.students} profiles` },
      { icon: 'internship', text: `${row.internships} listings` },
    ];
  }
  return [];
}

/** Mirrors the API's rules for a university, so the form says what is wrong before saving. */
function universityErrors(form) {
  const value = (key) => (form[key] ?? '').trim();
  const errors = {};
  if (value('name').length < 4) errors.name = 'The name must be at least 4 characters';
  else if (/\d/.test(value('name'))) errors.name = 'The name cannot contain digits';
  if (!value('city')) errors.city = 'City is required';
  if (!value('country')) errors.country = 'Country is required';
  return errors;
}

function EditDialog({ kind, tab, entry, isSaving, onCancel, onSave }) {
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [ready, setReady] = useState(null);

  // A skill files under a job category by name, so the choice comes from that
  // list rather than whatever somebody types.
  const { data: categoryData, isPending: categoriesPending } = useQuery({
    queryKey: ['reference', 'job-categories', 'options'],
    queryFn: () =>
      adminApi.listReference('job-categories', {
        page_size: 100,
        include_inactive: false,
        ...ALPHABETICAL,
      }),
    enabled: Boolean(entry) && kind === 'skills',
  });

  // Seed the form from whichever row was opened, once per opening.
  if (entry && ready !== entry) {
    setReady(entry);
    setTouched({});
    setForm({
      name: entry.name ?? '',
      country: entry.country ?? '',
      city: entry.city ?? '',
      email_domain: entry.email_domain ?? '',
      website: entry.website ?? '',
      category: entry.category ?? '',
    });
  }
  if (!entry) {
    if (ready !== null) setReady(null);
    return null;
  }

  const field = (key) => ({
    value: form[key] ?? '',
    onChange: (event) => setForm({ ...form, [key]: event.target.value }),
    onBlur: () => setTouched((current) => ({ ...current, [key]: true })),
  });

  const isUniversity = kind === 'universities';
  const errors = isUniversity ? universityErrors(form) : {};
  // Shown once a field has been left, so an empty form does not open in red.
  const errorFor = (key) => (touched[key] ? errors[key] : undefined);
  const isValid = isUniversity
    ? Object.keys(errors).length === 0
    : (form.name ?? '').trim().length >= 2;

  const body = () => {
    const trimmed = (key) => (form[key] ?? '').trim() || undefined;
    if (kind === 'universities') {
      return {
        name: trimmed('name'),
        country: trimmed('country'),
        city: trimmed('city'),
        email_domain: trimmed('email_domain'),
        website: trimmed('website'),
      };
    }
    if (kind === 'skills') {
      const category = trimmed('category');
      // Leaving a field out of a PATCH keeps it, so taking a category away
      // has to be said explicitly.
      const clearing = Boolean(entry.id && entry.category && !category);
      return {
        name: trimmed('name'),
        category,
        ...(clearing ? { clear_category: true } : {}),
      };
    }
    return { name: trimmed('name') };
  };

  return (
    <Modal
      open
      title={entry.id ? `Edit ${entry.name}` : `Add ${singular(tab.label).toLowerCase()}`}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button disabled={!isValid} isLoading={isSaving} onClick={() => onSave(body())}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Name" required={isUniversity} error={errorFor('name')} {...field('name')} />

        {kind === 'universities' && (
          <>
            <Field label="Email domain">
              <Input {...field('email_domain')} placeholder="uni-example.edu" />
            </Field>
            {/* The one field on this screen that is load-bearing rather than
                descriptive, so it says what it does. */}
            <p className="text-ink-muted text-xs">
              Students prove they study here by receiving a code at an address on this domain. Paste
              it however you have it — <code>@uni.edu</code> and <code>https://uni.edu/</code> both
              work.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="City" required error={errorFor('city')} {...field('city')} />
              <Input label="Country" required error={errorFor('country')} {...field('country')} />
            </div>
            <Field label="Website">
              <Input {...field('website')} />
            </Field>
          </>
        )}

        {kind === 'skills' && (
          <Select label="Job category" disabled={categoriesPending} {...field('category')}>
            <option value="">{categoriesPending ? 'Loading categories…' : 'No category'}</option>
            {categoryOptions(categoryData?.data ?? [], entry.category).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        )}

        {entry.id && (
          <Alert tone="info">
            The name changes; the address does not. <code>{entry.slug}</code> stays as it is, so
            saved links and filters keep working.
          </Alert>
        )}
      </div>
    </Modal>
  );
}

function sortParams(key) {
  const { sort, order } = SORTS.find((option) => option.key === key) ?? SORTS[0];
  return { sort, order };
}

/**
 * Active job category names, plus the skill's current category when it is not
 * one of them (retired, or typed before this was a dropdown) so opening the
 * form never silently changes it.
 */
function categoryOptions(rows, current) {
  const names = rows.map((row) => row.name);
  if (current && !names.includes(current)) names.unshift(current);
  return names;
}

/** Words too common to say two skill names are related. */
const STOP_WORDS = new Set(['and', 'of', 'the', 'for', 'with', 'in', 'to', 'a', '&']);

/** The most telling word of a name, to look up likely duplicates by. */
function keyWord(name) {
  const words = name
    .toLowerCase()
    .split(/[^a-z0-9+#.]+/)
    .filter((word) => word.length > 1 && !STOP_WORDS.has(word));
  return [...words].sort((a, b) => b.length - a.length)[0] ?? name;
}

/**
 * Combine a duplicate spelling into the skill the admin opened this from.
 *
 * Searchable rather than a dropdown of the first hundred names, so any
 * duplicate can be found; with nothing typed it suggests skills that share a
 * word with this one, which is where duplicates usually are.
 */
function MergeDialog({ skill, isMerging, onCancel, onConfirm }) {
  const [term, setTerm] = useState('');
  const [chosen, setChosen] = useState(null);

  const query = term.trim() || (skill ? keyWord(skill.name) : '');
  const { data, isFetching } = useQuery({
    queryKey: ['reference', 'skills', 'merge-candidates', query],
    queryFn: () =>
      adminApi.listReference('skills', {
        page_size: 20,
        include_inactive: false,
        q: query,
        ...ALPHABETICAL,
      }),
    enabled: Boolean(skill) && query.length > 0,
  });

  if (!skill) return null;
  const candidates = (data?.data ?? []).filter((row) => row.id !== skill.id);
  const suggesting = !term.trim();

  function close() {
    setTerm('');
    setChosen(null);
    onCancel();
  }

  return (
    <Modal
      open
      title="Combine duplicate skills"
      description={`Pick a skill that means the same as "${skill.name}". It is folded into "${skill.name}", which stays.`}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={isMerging}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!chosen}
            isLoading={isMerging}
            onClick={() => onConfirm(chosen.id)}
          >
            <Icon name="merge" size={15} />
            Combine skills
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-medium text-emerald-700">Keep</p>
            <p className="text-ink mt-0.5 text-sm font-semibold break-words">{skill.name}</p>
            <p className="text-ink-muted mt-0.5 text-xs">{skill.usage} in use</p>
          </div>
          <span className="text-ink-muted grid place-items-center" aria-hidden="true">
            <Icon name="arrowRight" size={18} className="rotate-180" />
          </span>
          <div
            className={cn(
              'rounded-xl border p-3',
              chosen ? 'border-danger-200 bg-danger-50' : 'border-line border-dashed',
            )}
          >
            <p className={cn('text-xs font-medium', chosen ? 'text-danger-600' : 'text-ink-muted')}>
              Remove
            </p>
            <p className="text-ink mt-0.5 text-sm font-semibold break-words">
              {chosen ? chosen.name : 'Choose below'}
            </p>
            {chosen && <p className="text-ink-muted mt-0.5 text-xs">{chosen.usage} in use</p>}
          </div>
        </div>

        <div>
          <SearchInput
            label="Find the duplicate"
            placeholder="Type a skill name"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onClear={() => setTerm('')}
          />
          <p className="text-ink-muted mt-2 text-xs font-medium">
            {suggesting ? 'Similar names' : 'Matching skills'}
          </p>
          <ul className="border-line divide-line mt-1.5 max-h-56 divide-y overflow-y-auto rounded-xl border">
            {isFetching && candidates.length === 0 ? (
              <li className="text-ink-muted px-3 py-3 text-sm">Searching…</li>
            ) : candidates.length === 0 ? (
              <li className="text-ink-muted px-3 py-3 text-sm">
                {suggesting
                  ? 'No similar names found. Type to search all skills.'
                  : 'No skill matches that search.'}
              </li>
            ) : (
              candidates.map((row) => {
                const selected = chosen?.id === row.id;
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setChosen(row)}
                      aria-pressed={selected}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition',
                        selected ? 'bg-brand-50' : 'hover:bg-surface-muted',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span
                          className={cn(
                            'grid size-4 shrink-0 place-items-center rounded-full border',
                            selected ? 'border-brand-600 bg-brand-600' : 'border-line',
                          )}
                        >
                          {selected && <span className="size-1.5 rounded-full bg-white" />}
                        </span>
                        <span className="text-ink truncate font-medium">{row.name}</span>
                      </span>
                      <span className="text-ink-muted shrink-0 text-xs">{row.usage} in use</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        {/* No button undoes this, so the outcome is spelled out with the real
            names and numbers before it is confirmed. */}
        {chosen ? (
          <Alert tone="warning" title="Check before you combine">
            <ul className="list-inside list-disc space-y-0.5">
              <li>
                Every profile and listing tagged &ldquo;{chosen.name}&rdquo; ({chosen.usage}) moves
                to &ldquo;{skill.name}&rdquo;.
              </li>
              <li>&ldquo;{chosen.name}&rdquo; is removed from the skills list.</li>
              <li>
                Anyone who types &ldquo;{chosen.name}&rdquo; later gets &ldquo;{skill.name}&rdquo;.
              </li>
              <li>This cannot be undone from here.</li>
            </ul>
          </Alert>
        ) : (
          <p className="text-ink-muted text-xs leading-5">
            Use this when two entries are the same skill spelled differently, for example
            &ldquo;JS&rdquo; and &ldquo;JavaScript&rdquo;. Nothing changes until you press Combine
            skills.
          </p>
        )}
      </div>
    </Modal>
  );
}
